<template>
  <div>
    <div data-cy="form-label" class="b-dialog__top-panel">
      <h2>{{getAssetName()}}{{getAssetComponentName()}}</h2>
      <button class="b-btn b-btn_border_none" @click="cancel">
        <dynamic-icon icon="close" />
      </button>
    </div>
    <form class="col-12 b-form b-dialog__form mt-5">
      <div class="row mb-5">
        <div class="col-8">
          <div class="b-form__container" v-if="isContractHasManualCharts">
            <label>{{$t('CONTENT.events.select_contract_event')}}</label>
            <cool-select
              :list="commandsForContractWithManualCharts"
              option-value="name"
              option-text="displayName"
              v-model="selectedCommandName"
              :placeholder="$t('CONTENT.choose')"/>
          </div>
          <div class="b-form__container" v-else>
            <div class="b-form__container_type_flex">
              <label>{{$t('CONTENT.events.select_contract_event')}}</label>
              <dynamic-icon class="mb-1 ml-1" v-tooltip="$t('CONTENT.hints.chooseAssetComponentEvent')" icon="question"></dynamic-icon>
            </div>
            <cool-select
              :list="commands"
              option-value="name"
              option-text="displayName"
              v-model="selectedCommandName"
              :placeholder="$t('CONTENT.choose')"/>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <AddPaymentParameters
            v-if="selectedCommandName === 'AddPaymentParameters'"
            ref="commandComponent"
            :assetComponent="assetComponent"/>

          <SetTermination
            v-if="selectedCommandName === 'SetTermination'"
            ref="commandComponent"
            :assetComponent="assetComponent"
            :currencyCodes="currencyCodes"/>

          <SetExtension
            v-if="selectedCommandName === 'SetExtension'"
            ref="commandComponent"
            :assetComponent="assetComponent" />

          <SetBuyout
            v-if="selectedCommandName === 'SetBuyout'"
            ref="commandComponent"
            :assetComponent="assetComponent"
            :currencyCodes="currencyCodes"/>

          <ChangeRentDuration
            v-if="selectedCommandName === 'ChangeRentDuration'"
            ref="commandComponent"
            :assetComponent="assetComponent"
            :endDate="assetComponent.endDate"/>

          <SetDevaluations
            v-if="selectedCommandName === 'SetDevaluations'"
            ref="commandComponent"
            :assetComponent="assetComponent"/>

          <SetManualChart
            v-if="selectedCommandName === 'SetManualChart'"
            :assetComponent="assetComponent"
            :assetComponentName="getAssetComponentName()"
            ref="commandComponent"/>

          <AddAssetComponent
            v-if="selectedCommandName === 'AddAssetComponent'"
            :assetId="assetComponent.assetId"
            ref="commandComponent"
            @isPaymentCorrect="updateFromValidationStatus"/>

          <ChangeIsPermanent
            v-if="selectedCommandName === 'ChangeIsPermanent'"
            ref="commandComponent"
            :assetComponent="assetComponent" />

          <ChangeQuantity
            v-if="selectedCommandName === 'ChangeQuantity'"
            ref="commandComponent"
            :assetComponent="assetComponent" />

          <ChangeLiquidationValue
            v-if="selectedCommandName === 'ChangeLiquidationValue'"
            ref="commandComponent"
            :assetComponent="assetComponent" />
        </div>
      </div>
      <div class="row">
        <div class="col-6">
          <div class="b-form__container b-form__container_type_flex">
            <button
              class="b-btn b-btn_type_primary"
              type="submit"
              :disabled="!isFormValid"
              @click="save($event)"
            >{{$t('CONTENT.save')}}</button>
            <button
              class="b-btn b-btn_type_empty"
              @click.prevent="cancel"
            >{{$t('CONTENT.cancel')}}</button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import SetTermination from "../../components/commands/SetTermination";
import SetExtension from "../../components/commands/SetExtension";
import SetBuyout from "../../components/commands/SetBuyout";
import ChangeRentDuration from "../../components/commands/ChangeRentDuration";
import AddPaymentParameters from "../../components/commands/AddPaymentParameters";
import ChangeIsPermanent from "../../components/commands/ChangeIsPermanent";
import ChangeQuantity from "../../components/commands/ChangeQuantity";
import ChangeLiquidationValue from "../../components/commands/ChangeLiquidationValue";
import { mapGetters } from "vuex";
import SetManualChart from "../commands/SetManualChart";
import AddAssetComponent from "../commands/AddAssetComponent";
import SetDevaluations from "../commands/SetDevaluations";

export default {
  components: {
    SetTermination,
    SetExtension,
    SetBuyout,
    ChangeRentDuration,
    ChangeIsPermanent,
    ChangeQuantity,
    AddPaymentParameters,
    SetManualChart,
    AddAssetComponent,
    SetDevaluations,
    ChangeLiquidationValue,
  },
  props: {
    assetComponent: {
      type: Object,
      required: true
    },
    currencyCodes: {
      required: true,
      type: Array
    }
  },

  data() {
    return {
      isFormValid: true,
      commands: [
        {
          displayName: this.$t("CONTENT.events.SetTermination"),
          name: "SetTermination"
        },
        {
          displayName: this.$t("CONTENT.events.SetExtension"),
          name: "SetExtension"
        },
        {
          displayName: this.$t("CONTENT.events.SetBuyout"),
          name: "SetBuyout"
        },
        {
          displayName: this.$t("CONTENT.events.ChangeRentDuration"),
          name: "ChangeRentDuration"
        },
        {
          displayName: this.$t("CONTENT.events.AddPaymentParameters"),
          name: "AddPaymentParameters"
        },
        {
          displayName: this.$t("CONTENT.events.ChangeIsPermanent"),
          name: "ChangeIsPermanent"
        },
        {
          displayName: this.$t("CONTENT.events.ChangeQuantity"),
          name: "ChangeQuantity"
        },
        {
          displayName: this.$t("CONTENT.events.AddAssetComponent"),
          name: "AddAssetComponent"
        },
        {
          displayName: this.$t("CONTENT.events.SetDevaluations"),
          name: "SetDevaluations"
        },
        {
          displayName: this.$t("CONTENT.events.ChangeLiquidationValue"),
          name: "ChangeLiquidationValue"
        },
        {
          displayName: this.$t("CONTENT.events.ReclassSecurityPayment"),
          name: "ReclassSecurityPayment"
        }
      ],
      commandsForContractWithManualCharts: [
        {
          displayName: this.$t("CONTENT.events.ChangeRentDuration"),
          name: "ChangeRentDuration"
        },
        {
          displayName: this.$t("CONTENT.events.ChangeIsPermanent"),
          name: "ChangeIsPermanent"
        },
        {
          displayName: this.$t("CONTENT.events.ChangeQuantity"),
          name: "ChangeQuantity"
        },
        {
          displayName: this.$t("CONTENT.events.SetManualChart"),
          name: "SetManualChart"
        },
        {
          displayName: this.$t("CONTENT.events.AddAssetComponent"),
          name: "AddAssetComponent"
        },
        {
          displayName: this.$t("CONTENT.events.ChangeLiquidationValue"),
          name: "ChangeLiquidationValue"
        }
      ],
      selectedCommandName: "ChangeRentDuration",
    };
  },
  computed: {
    ...mapGetters(["getAssets"]),
    ...mapGetters(["getCurrentContract"]),
    ...mapGetters(["eventRegistryPlugin/getAssetComponentLength"]),
    isContractHasManualCharts() {
      return this.getCurrentContract.hasManualCharts;
    },
    selectedAssetComponentLength() {
      return this.$store.getters['eventRegistryPlugin/getAssetComponentLength'];
    }
  },
  methods: {
    cancel() {
      this.$refs.commandComponent.cancel();
      this.$emit("closeModal");
    },

    save(e) {
      e.preventDefault();
      let saveResult = this.$refs.commandComponent.save();
      if(saveResult) {
        this.$emit("closeModal");
      }
    },
    getAssetName() {
      for (let i = 0; i < this.getAssets.length; i++) {
        if (this.getAssets[i].idInDB === this.assetComponent.assetId) {
          return this.getAssets[i].name;
        }
      }
    },
    getAssetComponentName() {
      if (this.assetComponent.name !== null) {
        return ` - ${this.assetComponent.name}`;
      }
    },
    updateFromValidationStatus(value) {
      this.isFormValid = value;
    }
  }
};
</script>
